// Shared UI components
import { useEffect, useRef } from 'react';
import { useData } from './DataContext.jsx';

function Avatar({ user, size = 28, badge }) {
  const color = user.color || user.avatar_color || 'linear-gradient(135deg,#FF6B4A,#FFB199)';
  const letter = user.avatar || user.initials || (user.full_name?.[0] ?? user.name?.[0] ?? '?');
  const s = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  const badgeSize = Math.max(14, Math.round(size * 0.55));
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: `0 0 ${size}px` }}>
      <div className="user-avatar" style={{ ...s, background: color }}>{letter}</div>
      {badge && (
        <div title={badge.title || ''} style={{
          position: 'absolute', right: -3, bottom: -3,
          width: badgeSize, height: badgeSize,
          borderRadius: '50%',
          background: 'var(--bg-elev)',
          border: '2px solid var(--bg-elev)',
          display: 'grid', placeItems: 'center',
          fontSize: Math.round(badgeSize * 0.6), lineHeight: 1,
        }}>
          {badge.emoji}
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === "complete" ? "var(--ok)" : status === "partial" ? "var(--warn)" : status === "not_started" ? "var(--crit)" : "var(--ink-4)";
  return <span className="dot-status" style={{ background: color }} />;
}

function ProgressRing({ pct, size = 128, stroke = 10, accent = "var(--accent)", children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (pct / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} stroke="var(--bg-hover)" strokeWidth={stroke} fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke={accent} strokeWidth={stroke} fill="none"
                strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 600ms cubic-bezier(0.2,0.8,0.2,1)' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
        {children}
      </div>
    </div>
  );
}

function BudgetLight({ pct, size = 10 }) {
  const tone = pct < 70 ? "var(--ok)" : pct < 90 ? "var(--warn)" : "var(--crit)";
  const bg = pct < 70 ? "var(--ok-soft)" : pct < 90 ? "var(--warn-soft)" : "var(--crit-soft)";
  return <span className="dot-status" style={{ width: size, height: size, background: tone, boxShadow: `0 0 0 4px ${bg}` }} />;
}

function ProjectAvatar({ project, size = 34 }) {
  const { customerById } = useData();
  const c = project ? customerById(project.customer_id) : null;

  // Inherit the customer's logo when nothing else is defined for the project.
  if (c?.logo_url) {
    return (
      <div style={{
        width: size, height: size, borderRadius: 9,
        background: '#fff',
        display: 'grid', placeItems: 'center',
        flex: `0 0 ${size}px`, overflow: 'hidden',
        border: '1px solid var(--stroke)',
      }}>
        <img src={c.logo_url} alt=""
             style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain', display: 'block' }}/>
      </div>
    );
  }

  const letter = c?.initial || project?.title?.[0] || '?';
  const color = project?.color || '#FF6B4A';
  return (
    <div style={{
      width: size, height: size, borderRadius: 9,
      background: `linear-gradient(135deg, ${color}, ${color}99)`,
      display: 'grid', placeItems: 'center',
      color: '#fff', fontWeight: 600, fontSize: Math.round(size * 0.44),
      flex: `0 0 ${size}px`,
    }}>{letter}</div>
  );
}

function SparkBar({ data, max, height = 64 }) {
  const hi = max || Math.max(...data.map(d => d.minutes), 480);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height, marginTop: 4 }}>
      {data.map((d, i) => {
        const h = d.minutes ? Math.max(4, (d.minutes / hi) * (height - 18)) : 3;
        const color = d.today ? 'var(--accent)'
          : d.status === 'weekend' ? 'var(--bg-hover)'
          : d.status === 'complete' ? 'var(--ink-2)'
          : 'var(--warn)';
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: '100%', height: h, background: color, borderRadius: 4,
              opacity: d.status === 'weekend' ? 0.4 : 1,
              transition: 'height 400ms ease',
            }}/>
            <div style={{ fontSize: 10.5, color: d.today ? 'var(--accent)' : 'var(--ink-4)', fontWeight: d.today ? 600 : 500 }}>{d.day}</div>
          </div>
        );
      })}
    </div>
  );
}

// Toast manager
function Toasts({ toasts }) {
  return (
    <div className="toasts">
      {toasts.map(t => (
        <div key={t.id} className="toast">
          {t.emoji && <div style={{ fontSize: 18 }}>{t.emoji}</div>}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{t.title}</div>
            {t.body && <div className="tiny muted" style={{ marginTop: 2 }}>{t.body}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Confetti({ trigger }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!trigger) return;
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;
    const colors = ['#FF6B4A','#10B981','#F59E0B','#6366F1','#EC4899','#F2F3F5'];
    const parts = Array.from({ length: 140 }, () => ({
      x: W/2 + (Math.random()-0.5)*60,
      y: H/2 - 20,
      vx: (Math.random()-0.5) * 14,
      vy: -Math.random() * 16 - 6,
      g: 0.45 + Math.random()*0.2,
      s: 5 + Math.random()*5,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random()-0.5) * 0.3,
      color: colors[Math.floor(Math.random()*colors.length)],
    }));
    let t = 0;
    let raf;
    const tick = () => {
      t++;
      ctx.clearRect(0, 0, W, H);
      parts.forEach(p => {
        p.vy += p.g; p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.s/2, -p.s/2, p.s, p.s*0.6);
        ctx.restore();
      });
      if (t < 180) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0,0,W,H);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [trigger]);
  return <canvas id="confetti-canvas" ref={ref}/>;
}

function Modal({ onClose, children }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">{children}</div>
    </div>
  );
}

export {
  Avatar, StatusDot, ProgressRing, BudgetLight, ProjectAvatar, SparkBar, Toasts, Confetti, Modal
};
